import { useAuth0 } from '@auth0/auth0-react'
import { Auth0Config, ContentAdminConfig } from 'config'
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { useUserStore } from 'store'
import { ContentLoading } from './content-loading'
import { useTrackPostHogEvent } from 'utils/posthog'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { applyTheme, getStoredPreference, transitionTheme } from 'utils/theme'
import { Users } from 'services/user'
import { ChatService } from 'services/chat'

import { contentReturnPath } from './content-return'
import {
	contentFrameUrl,
	contentServiceOrigin,
	readerLocationPath,
	readerQuery
} from './content-url'
import { useChangelogAnnouncementSeen } from './content-announcements'
export { contentFrameUrl } from './content-url'

export const ContentAdminPage = ({
	readerPath,
	dashboardUrl,
	onDashboard,
	onReaderNavigate
}: {
	readerPath?: string
	dashboardUrl?: string
	onDashboard?: () => void
	onReaderNavigate?: (path: string) => void
}) => {
	const { getAccessTokenSilently, user: authUser } = useAuth0()
	const user = useUserStore((state) => state.user)
	const level = user?.admin?.access_level
	const allowed = readerPath ? !!user : level === 'FULL' || level === 'SUPER'
	useEffect(() => {
		if (allowed && readerPath)
			void ChatService.initializeSupport().catch(() => {})
	}, [allowed, readerPath])
	const label = readerPath ? 'documentation' : 'content administration'
	const title = readerPath
		? 'ClickGuard documentation'
		: 'Docs and Changelog content administration'
	const track = useTrackPostHogEvent()
	const tracked = useRef(new Set<string>())
	const frame = useRef<HTMLIFrameElement>(null)
	const [src, setSrc] = useState('')
	const [error, setError] = useState(false)
	const [ready, setReady] = useState(false)
	const [changelogOpened, setChangelogOpened] = useState(false)
	const [supportError, setSupportError] = useState(false)
	const opened = useRef(false)
	const [attempt, setAttempt] = useState(0)
	const reportedPath = useRef<{
		path: string
		subject?: string
		attempt: number
	}>()
	const getToken = useCallback(
		() =>
			getAccessTokenSilently({
				audience: Auth0Config.audience,
				scope: Auth0Config.scope
			}),
		[getAccessTokenSilently]
	)
	useChangelogAnnouncementSeen(
		ready && !!src,
		changelogOpened ? '/docs/changelog' : readerPath,
		getToken,
		authUser?.sub
	)
	useEffect(() => {
		if (!allowed) return
		// The iframe already navigated. Updating the address bar must not remount it
		// or start another Auth0 exchange. Real outer navigation/retry/identity changes
		// still use the normal authenticated entry bridge.
		if (
			readerPath &&
			opened.current &&
			reportedPath.current?.path === readerPath &&
			reportedPath.current.subject === authUser?.sub &&
			reportedPath.current.attempt === attempt
		)
			return
		let cancelled = false
		setError(false)
		setReady(false)
		setChangelogOpened(false)
		opened.current = false
		setSrc('')
		try {
			contentServiceOrigin(ContentAdminConfig.url, window.location.origin)
		} catch {
			setError(true)
			return
		}
		const timeout = window.setTimeout(() => {
			if (!opened.current) setError(true)
		}, 30000)
		getToken()
			.then((token) => {
				if (!cancelled)
					setSrc(
						contentFrameUrl(
							ContentAdminConfig.url,
							token,
							window.location.origin,
							readerPath
						)
					)
			})
			.catch(() => {
				if (!cancelled) setError(true)
			})
		return () => {
			cancelled = true
			window.clearTimeout(timeout)
		}
	}, [allowed, getToken, attempt, readerPath, authUser?.sub])
	// Subscribe during commit, before a fast iframe can emit readiness/tracking messages.
	useLayoutEffect(() => {
		if (!src) return
		let stopped = false
		let pendingToken: Promise<string> | undefined
		const origin = new URL(src).origin
		const sendTheme = () =>
			frame.current?.contentWindow?.postMessage(
				{
					type: 'cg-kb:theme',
					preference: getStoredPreference(),
					resolved:
						document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
				},
				origin
			)
		// Observe the actual applied theme: this also covers System/OS changes,
		// feature-flag updates and preference changes with the same resolved color.
		const themeObserver = new MutationObserver(sendTheme)
		themeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['data-theme']
		})
		const sendDashboard = () => {
			if (readerPath && dashboardUrl)
				frame.current?.contentWindow?.postMessage(
					{ type: 'cg-kb:dashboard-context', href: dashboardUrl },
					origin
				)
		}
		const receive = async (event: MessageEvent) => {
			if (
				event.origin !== origin ||
				event.source !== frame.current?.contentWindow
			)
				return
			if (event.data?.type === 'cg-kb:ready') {
				sendTheme()
				sendDashboard()
				opened.current = true
				setReady(true)
				setError(false)
				return
			}
			if (event.data?.type === 'cg-kb:dashboard' && readerPath) {
				onDashboard?.()
				return
			}
			if (
				event.data?.type === 'cg-kb:support' &&
				readerPath &&
				opened.current
			) {
				setSupportError(false)
				void ChatService.openSupport().catch(() => setSupportError(true))
				return
			}
			if (
				event.data?.type === 'cg-kb:location' &&
				readerPath &&
				opened.current
			) {
				const path = readerLocationPath(event.data.path)
				if (path) {
					reportedPath.current = { path, subject: authUser?.sub, attempt }
					setChangelogOpened(/^\/docs\/changelog(?:\/|\?|$)/.test(path))
					onReaderNavigate?.(path)
				}
				return
			}
			if (event.data?.type === 'cg-kb:changelog-opened' && readerPath) {
				// Compatibility with CMS revisions preceding location synchronization.
				setChangelogOpened(true)
				return
			}
			if (event.data?.type === 'cg-kb:theme-preference') {
				const preference = event.data.preference
				if (
					preference === 'light' ||
					preference === 'dark' ||
					preference === 'system'
				) {
					transitionTheme(preference)
					// A nested reader control must honor the platform's dark-mode flag too.
					applyTheme(getStoredPreference())
					Users.updateTheme(preference).catch(() => undefined)
				}
				return
			}
			if (event.data?.type === 'cg-kb:error') {
				// Once open, the CMS preserves unsaved edits and handles renewal in place.
				if (!opened.current) setError(true)
				return
			}
			if (event.data?.type === 'cg-kb:track' && readerPath) {
				const { eventId, event: name, eventPath, parameters } = event.data
				if (
					typeof eventId !== 'string' ||
					!/^[a-f0-9-]{36}$/.test(eventId) ||
					tracked.current.has(eventId) ||
					!['docs', 'changelog'].includes(eventPath)
				)
					return
				const value =
					name === 'pageview'
						? parameters?.slug
						: name === 'empty_search'
						? parameters?.query
						: undefined
				if (typeof value !== 'string' || !value.trim() || value.length > 200)
					return
				tracked.current.add(eventId)
				track(
					name,
					eventPath,
					name === 'pageview' ? { slug: value } : { query: value }
				)
				return
			}
			if (event.data?.type !== 'cg-kb:token-request') return
			const requestId = event.data.requestId
			if (
				requestId !== undefined &&
				(typeof requestId !== 'string' || requestId.length > 100)
			)
				return
			const correlation = requestId === undefined ? {} : { requestId }
			sendTheme()
			const target = frame.current.contentWindow
			try {
				// A cached source JWT can be almost expired even while the platform is signed in.
				// Coalesce wake/retry messages, but obtain a genuinely fresh token for renewal.
				if (!pendingToken) {
					const requested = getAccessTokenSilently({
						audience: Auth0Config.audience,
						scope: Auth0Config.scope,
						ignoreCache: true,
						timeoutInSeconds: 45
					})
					pendingToken = requested
					const clear = () => {
						if (pendingToken === requested) pendingToken = undefined
					}
					requested.then(clear, clear)
				}
				const token = await pendingToken
				if (stopped || target !== frame.current?.contentWindow) return
				target?.postMessage(
					{ type: 'cg-kb:token', token, ...correlation },
					origin
				)
			} catch {
				if (!stopped && target === frame.current?.contentWindow)
					target?.postMessage(
						{ type: 'cg-kb:auth-error', ...correlation },
						origin
					)
			}
		}
		window.addEventListener('message', receive)
		sendTheme()
		return () => {
			stopped = true
			themeObserver.disconnect()
			window.removeEventListener('message', receive)
		}
	}, [
		src,
		getAccessTokenSilently,
		readerPath,
		track,
		authUser?.sub,
		attempt,
		dashboardUrl,
		onDashboard,
		onReaderNavigate
	])
	if (!allowed)
		return (
			<p role="alert" className="p-6">
				Content administration requires FULL or SUPER Admin access.
			</p>
		)
	return (
		<section
			aria-label={readerPath ? 'Documentation' : 'Content administration'}
			aria-busy={!ready && !error}
			className="ph-no-capture relative flex h-full min-h-[400px] w-full flex-col"
		>
			{supportError && (
				<div
					role="alert"
					className="absolute right-4 top-4 z-10 rounded-lg border cg-bg-surface-1 cg-text-text-primary p-4 shadow-lg"
				>
					Chat could not be opened.{' '}
					<a
						href="https://share.hsforms.com/2S7T3Ztt9SVK7nkgcyrAnPwu4crx"
						target="_blank"
						rel="noopener noreferrer"
						className="underline"
					>
						Submit a request
					</a>
				</div>
			)}
			{error ? (
				<div role="alert" className="p-6">
					<p>Unable to open {label}. Check your sign-in and try again.</p>
					<button
						className="mt-3 underline"
						onClick={() => setAttempt((value) => value + 1)}
					>
						Try again
					</button>
				</div>
			) : !ready ? (
				<div className="absolute inset-0">
					<ContentLoading
						label={label}
						reader={!!readerPath}
						readerPath={readerPath}
					/>
				</div>
			) : null}
			{src && !error && (
				<iframe
					ref={frame}
					src={src}
					title={title}
					referrerPolicy="no-referrer"
					className={`h-full min-h-[400px] flex-1 w-full border-0 ${
						ready ? '' : 'invisible'
					}`}
					onError={() => setError(true)}
					allow="clipboard-write"
				/>
			)}
		</section>
	)
}

// All reader entrypoints belong above MainPage/MainLayout, never inside a dashboard.
export const contentReaderPaths = [
	'/docs/*',
	'/reader/*',
	'/changelog/*'
] as const

export const ContentReaderPage = ({
	standalone = false
}: {
	standalone?: boolean
}) => {
	const { pathname, search } = useLocation()
	const navigate = useNavigate()
	const { user } = useAuth0()
	const returnPath = contentReturnPath(user?.sub)
	const onDashboard = useCallback(
		() => navigate(returnPath),
		[navigate, returnPath]
	)
	const onReaderNavigate = useCallback(
		(path: string) => {
			if (path !== `${pathname}${readerQuery(search)}`) {
				// Iframe navigation already added the joint browser-history entry.
				// Replace its parent URL rather than adding a duplicate Back step.
				navigate(path, { replace: true })
			}
		},
		[navigate, pathname, search]
	)
	const path = standalone
		? pathname.replace(/^\/reader(?=\/|$)/, '/docs')
		: pathname.startsWith('/changelog')
		? `/docs${pathname}`
		: pathname
	return (
		<ContentAdminPage
			readerPath={`${path}${readerQuery(search)}`}
			dashboardUrl={window.location.origin + returnPath}
			onDashboard={onDashboard}
			onReaderNavigate={onReaderNavigate}
		/>
	)
}

/** Same Auth0/renewal bridge, with no account dashboard or second navigation shell. */
export const StandaloneContentReaderPage = () => {
	const { pathname, search } = useLocation()
	const canonical = pathname
		.replace(/^\/reader(?=\/|$)/, '/docs')
		.replace(/^\/changelog(?=\/|$)/, '/docs/changelog')
	const query = readerQuery(search)
	if (canonical !== pathname || query !== search)
		return <Navigate to={`${canonical}${query}`} replace />

	return (
		<div className="h-[100dvh] w-full overflow-hidden">
			<ContentReaderPage standalone />
		</div>
	)
}
